import React, { useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { apifetch } from '../services/interceptors';
import { downloadRecoveryCodes } from '../utils/downloadRecoveryCodes';

// Full-page, non-dismissible MFA setup shown to new accounts. Authenticator app is the
// default/recommended sign-in method, but the user must make an explicit choice — set up
// an authenticator app, OR opt in to emailed codes — before they can continue. There is
// deliberately no silent skip: one of the two options must be completed.
const MandatoryMfaSetup = ({ onComplete }) => {
  const [choice, setChoice] = useState(null); // null | 'totp' | 'email'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [setup, setSetup] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [downloaded, setDownloaded] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const chooseAuthenticator = async () => {
    setChoice('totp');
    setBusy(true); setError('');
    try {
      const res = await apifetch('s1/api/mfa/totp/setup', { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start setup');
      setSetup(data);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    setBusy(true); setError('');
    try {
      const res = await apifetch('s1/api/mfa/totp/verify-setup', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ code: confirmCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to verify code');
      setRecoveryCodes(data.recoveryCodes || []);
      setSetup(null);
      setConfirmCode('');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Explicit opt-in to emailed codes instead of an authenticator app — still requires an
  // active choice (this call), it just isn't the recommended path.
  const chooseEmail = async () => {
    setChoice('email');
    setBusy(true); setError('');
    try {
      const res = await apifetch('s1/api/mfa/method', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ method: 'email' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to opt in to email codes');
      onComplete('email');
    } catch (e) { setError(e.message); setChoice(null); } finally { setBusy(false); }
  };

  const backToChoice = () => {
    setChoice(null);
    setSetup(null);
    setConfirmCode('');
    setError('');
  };

  return (
    <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <Card className="shadow-lg border-0 rounded-4 p-4" style={{ maxWidth: 480, width: '100%' }}>
        <Card.Body>
          <h3 className="fw-bold mb-2">Secure your account</h3>
          <p className="text-muted small mb-3">
            Choose how you'll verify sign-ins. An <strong>authenticator app</strong> (Google Authenticator, Microsoft
            Authenticator, or Duo) is recommended — it's faster and more reliable than waiting for an email. This is
            a required, one-time step before you can continue.
          </p>

          {error && <Alert variant="danger">{error}</Alert>}

          {/* ── Step 1: explicit choice ─────────────────────────────────────── */}
          {choice === null && (
            <div className="d-grid gap-2">
              <Button variant="primary" onClick={chooseAuthenticator} disabled={busy}>
                {busy ? <Spinner animation="border" size="sm" /> : '📱 Set up authenticator app (recommended)'}
              </Button>
              <Button variant="outline-secondary" onClick={chooseEmail} disabled={busy}>
                {busy ? <Spinner animation="border" size="sm" /> : '✉️ Use emailed codes instead'}
              </Button>
            </div>
          )}

          {/* ── Step 2a: authenticator enrollment ───────────────────────────── */}
          {choice === 'totp' && setup && (
            <div className="p-3 border rounded mb-3">
              <p className="mb-2">1. Scan this QR code with your authenticator app:</p>
              <div className="text-center mb-2">
                <img src={setup.qr} alt="Authenticator QR code" style={{ width: 180, height: 180 }} />
              </div>
              <p className="small text-muted mb-2">
                Can’t scan? Enter this key manually: <code>{setup.secret}</code>
              </p>
              <Form.Group className="mb-2">
                <Form.Label>2. Enter the 6-digit code from the app to confirm</Form.Label>
                <Form.Control
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  placeholder="123456"
                  autoFocus
                />
              </Form.Group>
              <Button className="w-100 mb-2" onClick={confirmSetup} disabled={busy || !confirmCode.trim()}>
                {busy ? <Spinner animation="border" size="sm" /> : 'Confirm & continue'}
              </Button>
              <Button variant="link" size="sm" className="p-0" onClick={backToChoice} disabled={busy}>
                ← Use emailed codes instead
              </Button>
            </div>
          )}

          {choice === 'totp' && !setup && !recoveryCodes && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Preparing setup…
            </div>
          )}

          {/* ── Step 2b: recovery codes (authenticator path only) ───────────── */}
          {recoveryCodes && (
            <>
              <Alert variant="warning">
                <strong>Save your recovery codes.</strong> Each can be used once to sign in if you lose your device.
                They won’t be shown again.
                <pre className="mt-2 mb-2" style={{ fontFamily: 'monospace' }}>{recoveryCodes.join('\n')}</pre>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => { downloadRecoveryCodes(recoveryCodes); setDownloaded(true); }}
                >
                  ⬇ Download codes (.txt)
                </Button>
              </Alert>
              <Button
                className="w-100"
                variant={downloaded ? 'success' : 'outline-secondary'}
                onClick={() => onComplete('totp')}
              >
                {downloaded ? 'Continue to Pipsqueak' : "I've saved my codes — continue"}
              </Button>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MandatoryMfaSetup;
